import { Controller } from "@hotwired/stimulus"

// The Clean Items run list: ticked linked rows plus added rows make up the run. Searching a cleaner
// ticks its row when it's already listed, otherwise appends a server-rendered row via htmx.
// Provider runs also list catalogs; Run then needs at least one ticked.
export default class extends Controller {
  static targets = ["checkbox", "added", "list", "search", "association", "button", "empty", "catalog"]
  static values = { rowUrl: String, name: String }

  initialize() {
    this.pending = new Set()
  }

  connect() {
    // The form wraps this element, so its submit event never reaches a data-action here
    this.form = this.element.closest("form")
    this.form?.addEventListener("submit", this.submitting)
    this.update()
  }

  disconnect() {
    this.form?.removeEventListener("submit", this.submitting)
  }

  // Disable Run once submitted so a double-click can't post a second run
  submitting = () => {
    this.submitted = true
    this.update()
  }

  checkboxTargetConnected() { this.update() }
  addedTargetConnected() { this.update() }
  addedTargetDisconnected() { this.update() }

  add() {
    const uuid = this.searchTarget.value
    // Clearing the search re-fires select-two:change with no value
    if (!uuid) return

    this.resetSearch()
    const row = this.rowFor(uuid)
    if (row) {
      this.tick(row)
      return
    }
    if (this.pending.has(uuid)) return

    this.pending.add(uuid)
    const url = this.rowUrlValue.replace("__uuid__", encodeURIComponent(uuid))
    Promise.resolve(window.htmx.ajax("GET", url, { target: this.listTarget, swap: "beforeend" }))
      .finally(() => this.pending.delete(uuid))
  }

  remove(event) {
    event.target.closest("tr").remove()
  }

  selectAll() {
    this.checkboxTargets.forEach(checkbox => { checkbox.checked = true })
    this.update()
  }

  clear() {
    this.checkboxTargets.forEach(checkbox => { checkbox.checked = false })
    this.update()
  }

  selectAllCatalogs() {
    this.catalogTargets.forEach(checkbox => { checkbox.checked = true })
    this.update()
  }

  clearCatalogs() {
    this.catalogTargets.forEach(checkbox => { checkbox.checked = false })
    this.update()
  }

  update() {
    if (!this.hasButtonTarget) return

    const count = this.checkboxTargets.filter(checkbox => checkbox.checked).length + this.addedTargets.length
    const cleaners = `${count} ${count === 1 ? "cleaner" : "cleaners"}`
    if (this.hasCatalogTarget) {
      const catalogs = this.catalogTargets.filter(checkbox => checkbox.checked).length
      this.buttonTarget.disabled = this.submitted || count === 0 || catalogs === 0
      this.buttonTarget.textContent = `Run ${cleaners} on ${catalogs} ${catalogs === 1 ? "catalog" : "catalogs"} of ${this.nameValue}`
    } else {
      this.buttonTarget.disabled = this.submitted || count === 0
      this.buttonTarget.textContent = `Run ${cleaners} on ${this.nameValue}`
    }
    if (this.hasAssociationTarget) this.associationTarget.hidden = this.addedTargets.length === 0
    if (this.hasEmptyTarget) this.emptyTarget.hidden = this.checkboxTargets.length + this.addedTargets.length > 0
  }

  rowFor(uuid) {
    return Array.from(this.listTarget.rows).find(row => row.dataset.uuid === uuid)
  }

  tick(row) {
    const checkbox = row.querySelector("input[type=checkbox]")
    if (checkbox) checkbox.checked = true
    this.update()
    row.classList.add("table-warning")
    setTimeout(() => row.classList.remove("table-warning"), 1500)
  }

  resetSearch() {
    window.$(this.searchTarget).val(null).trigger("change")
  }
}
